import React, { useState, useEffect } from "react";
import { authFetch } from "../pages/Auth";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { COLORS, RISK_COLORS, commonAxisStyle, commonGridStyle, tooltipStyle, cardStyle } from "../chartTheme";

export default function PatientComparison({ patientA, patientB, onClose }) {
  const [detailA, setDetailA] = useState(null);
  const [detailB, setDetailB] = useState(null);
  const [obsA, setObsA] = useState([]);
  const [obsB, setObsB] = useState([]);
  const [riskA, setRiskA] = useState(null);
  const [riskB, setRiskB] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientA?.patient_id || !patientB?.patient_id) return;
    setLoading(true);
    Promise.all([
      authFetch(`/api/patients/${patientA.patient_id}`).then(r => r.json()),
      authFetch(`/api/patients/${patientB.patient_id}`).then(r => r.json()),
      authFetch(`/api/patients/${patientA.patient_id}/observations?limit=100`).then(r => r.json()),
      authFetch(`/api/patients/${patientB.patient_id}/observations?limit=100`).then(r => r.json()),
      authFetch(`/api/ml/dropout-risk/${patientA.patient_id}`).then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch(`/api/ml/dropout-risk/${patientB.patient_id}`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([dA, dB, oA, oB, rA, rB]) => {
      setDetailA(dA?.patient || patientA);
      setDetailB(dB?.patient || patientB);
      setObsA(oA?.observations || []);
      setObsB(oB?.observations || []);
      setRiskA(rA);
      setRiskB(rB);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [patientA, patientB]);

  if (!patientA || !patientB) return null;

  const weightsA = obsA.filter(o => o.observation_type === "body_weight").sort((a, b) => (a.observation_date || "").localeCompare(b.observation_date || ""));
  const weightsB = obsB.filter(o => o.observation_type === "body_weight").sort((a, b) => (a.observation_date || "").localeCompare(b.observation_date || ""));

  // Merge weight data for chart
  const allDates = new Set([...weightsA.map(w => w.observation_date?.slice(0, 7)), ...weightsB.map(w => w.observation_date?.slice(0, 7))]);
  const chartData = [...allDates].sort().map(month => ({
    month,
    patientA: weightsA.find(w => w.observation_date?.startsWith(month))?.value_quantity ?? null,
    patientB: weightsB.find(w => w.observation_date?.startsWith(month))?.value_quantity ?? null,
  }));

  const pA = detailA || patientA;
  const pB = detailB || patientB;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Patient Comparison</div>
          <button onClick={onClose} style={s.closeBtn}>&times;</button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: COLORS.muted }}>Loading comparison...</div>
        ) : (
          <>
            {/* Side by side demographics */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <PatientCard patient={pA} risk={riskA} label="Patient A" color={COLORS.primary} obsCount={obsA.length} />
              <PatientCard patient={pB} risk={riskB} label="Patient B" color={COLORS.secondary} obsCount={obsB.length} />
            </div>

            {/* Metric comparison table */}
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Metric Comparison</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={s.th}>Metric</th>
                    <th style={{ ...s.th, color: COLORS.primary }}>Patient A</th>
                    <th style={{ ...s.th, color: COLORS.secondary }}>Patient B</th>
                  </tr>
                </thead>
                <tbody>
                  <CompRow label="Program" a={pA.program_name} b={pB.program_name} />
                  <CompRow label="BMI at Enrollment" a={pA.bmi_at_enrollment} b={pB.bmi_at_enrollment} lower />
                  <CompRow label="Weight at Enrollment" a={pA.weight_at_enrollment_lbs ? `${pA.weight_at_enrollment_lbs} lbs` : null} b={pB.weight_at_enrollment_lbs ? `${pB.weight_at_enrollment_lbs} lbs` : null} />
                  <CompRow label="Current Weight" a={weightsA.length > 0 ? `${weightsA[weightsA.length-1].value_quantity} lbs` : null} b={weightsB.length > 0 ? `${weightsB[weightsB.length-1].value_quantity} lbs` : null} lower />
                  <CompRow label="Weight Change"
                    a={weightsA.length > 1 ? `${((weightsA[weightsA.length-1].value_quantity - weightsA[0].value_quantity) / weightsA[0].value_quantity * 100).toFixed(1)}%` : null}
                    b={weightsB.length > 1 ? `${((weightsB[weightsB.length-1].value_quantity - weightsB[0].value_quantity) / weightsB[0].value_quantity * 100).toFixed(1)}%` : null}
                    lower />
                  <CompRow label="Risk Score" a={riskA?.dropout_risk_score != null ? (riskA.dropout_risk_score * 100).toFixed(0) : null} b={riskB?.dropout_risk_score != null ? (riskB.dropout_risk_score * 100).toFixed(0) : null} lower />
                  <CompRow label="Risk Category" a={riskA?.risk_category} b={riskB?.risk_category} />
                  <CompRow label="Observations" a={obsA.length} b={obsB.length} />
                  <CompRow label="Status" a={pA.is_active ? "Active" : "Inactive"} b={pB.is_active ? "Active" : "Inactive"} />
                </tbody>
              </table>
            </div>

            {/* Weight trajectory comparison chart */}
            {chartData.length > 0 && (
              <div style={cardStyle}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Weight Trajectory Comparison</div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData}>
                    <CartesianGrid {...commonGridStyle} />
                    <XAxis dataKey="month" tick={commonAxisStyle} axisLine={false} tickLine={false} />
                    <YAxis tick={commonAxisStyle} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                    <Tooltip {...tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="patientA" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} name={`${pA.given_name} ${pA.family_name}`} connectNulls />
                    <Line type="monotone" dataKey="patientB" stroke={COLORS.secondary} strokeWidth={2} dot={{ r: 3 }} name={`${pB.given_name} ${pB.family_name}`} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PatientCard({ patient, risk, label, color, obsCount }) {
  const p = patient;
  const riskColor = risk ? (RISK_COLORS[risk.risk_category] || COLORS.muted) : COLORS.muted;
  return (
    <div style={{ ...cardStyle, borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 9, color, fontFamily: "monospace", letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{p.given_name} {p.family_name}</div>
      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 10 }}>
        {p.gender} &middot; {p.state || ""} &middot; {p.program_name || ""}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: p.is_active ? "rgba(52,211,153,0.15)" : "rgba(251,191,36,0.15)", color: p.is_active ? "#34d399" : "#fbbf24" }}>
          {p.is_active ? "Active" : "Inactive"}
        </span>
        {risk && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: `${riskColor}18`, color: riskColor }}>
            {risk.risk_category} risk
          </span>
        )}
      </div>
    </div>
  );
}

function CompRow({ label, a, b, lower }) {
  const aVal = a ?? "\u2014";
  const bVal = b ?? "\u2014";
  return (
    <tr>
      <td style={s.td}>{label}</td>
      <td style={{ ...s.tdVal, color: COLORS.text }}>{aVal}</td>
      <td style={{ ...s.tdVal, color: COLORS.text }}>{bVal}</td>
    </tr>
  );
}

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center", padding: 20 },
  panel: { width: "100%", maxWidth: 800, maxHeight: "90vh", background: "#0d1117", border: `1px solid ${COLORS.border}`, borderRadius: 12, overflowY: "auto", padding: 24 },
  closeBtn: { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.muted, padding: "8px 12px", fontSize: 13, cursor: "pointer" },
  th: { textAlign: "left", padding: "6px 10px", fontSize: 9, fontWeight: 600, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "8px 10px", fontSize: 11, color: COLORS.muted, borderBottom: `1px solid rgba(255,255,255,0.04)` },
  tdVal: { padding: "8px 10px", fontSize: 12, fontWeight: 600, borderBottom: `1px solid rgba(255,255,255,0.04)` },
};
